import {
  ColumnDef,
  ExpandedState,
  functionalUpdate,
  PaginationState,
  Row,
  SortingState,
  Updater,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { shiftSelect } from 'src/components/Events/EventPage/EventPage.css';
import { useSingleTableShortcutSelection } from 'src/components/Events/EventPage/hooks/useSingleTableShortcutSelection';
import { useActiveSortTableColumnContext } from 'src/contexts/ActiveSortTableColumnContext';
import { useAppContext } from 'src/contexts/AppContext';
import { ColumnResizingContextProvider } from 'src/contexts/ColumnResizingContext/ColumnResizingContext';
import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import {
  MultiSelectScope,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { useClearShiftSelectionOnSortingChange } from 'src/contexts/MultiSelectionContext/useClearShiftSelectionOnSortingChange';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { useSyncCenterSettings } from 'src/hooks/api/useSyncCenterSetting';
import { useEnableSelectionByDefault } from 'src/hooks/useEnableSelectionByDefault';
import { useUserHasTableTotalsFeature } from 'src/hooks/useUserHasFeature';
import { PurchaseOrderTableProps } from 'src/tables/PurchaseTable/PurchaseTable.types';
import { useGetPurchasesTableColumns } from 'src/tables/PurchaseTicketTable/useGetPurchasesTableColumns';
import { NoData } from 'src/tables/Table';
import { Table } from 'src/tables/TableV2/Table';
import { PurchasesTableColumnId } from 'src/utils/columns/purchases/purchasesColumnUtils.types';
import { MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { isInFullPageEventView } from 'src/utils/deepLinkUtils';
import { createAggregatedPurchaseOrderTicketGroup } from 'src/utils/purchaseUtils';
import { SectionType } from 'src/utils/types/sectionType';
import { SomethingWentWrong } from 'src/views';
import {
  ActionOutboxEntityType,
  PurchaseOrderTicketGroup,
  PurchaseViewMode,
  UserSetting,
} from 'src/WebApiController';

import { useSeatingColumnSorting } from '../common/hooks/useSeatingColumnSorting';
import { usePurchaseTableData } from './usePurchaseTableData';
import { usePurchaseTableSelection } from './usePurchaseTableSelection';

const PAGE_SIZE = 10;

export function PurchaseOrderTableV2({
  groupId,
  entities,
  entitiesSummary,
  entityCount,
  getDataFail,
  onMount,
  onUnmount,
  initState,
  disablePagination,
  useVirtuoso,
  viewMode = PurchaseViewMode.TileView,
  isItemsLoading,
  columnVisibilityOverride,
  formatOptionOverrides,
  withOuterPadding = true,
  disableSelection,
  isSideTable,
  alwaysShowCheckbox,
  showSelectAllOnHeader,
  onItemSelectionsChanged,
  rowSupportsAccordion,
  ignoreMaxCount,
  onPaginationChange: onPaginationChangeCallback,
  onSortingChange: onSortingChangeCallback,
  serverSidePageIndex,
  manualPagination,
  rowCount,
  pageSize,
}: PurchaseOrderTableProps) {
  const enableSelectionByDefault = useEnableSelectionByDefault();
  alwaysShowCheckbox = alwaysShowCheckbox || enableSelectionByDefault;

  const settings = useSyncCenterSettings();
  const { isAutoPoQualityControlled = false } = settings.data ?? {};

  const purchasesText = useContent(ContentId.Purchases);

  const hasTableTotalsFeature = useUserHasTableTotalsFeature(
    ActionOutboxEntityType.Purchase
  );

  const { selectionMode, getSelection } = useMultiSelectionContext();
  const purchaseSelection = getSelection(groupId);

  const [expandedState, setExpandedState] = useState<ExpandedState>({});

  const { sorting, setSorting } = useActiveSortTableColumnContext();

  useClearShiftSelectionOnSortingChange({ sortingState: sorting });

  const sectionType = isSideTable
    ? SectionType.PurchaseSideTable
    : viewMode === PurchaseViewMode.EventTileView
      ? SectionType.PurchaseEvent
      : SectionType.Purchases;

  const seatingColumnSorting =
    useSeatingColumnSorting<PurchaseOrderTicketGroup>(
      PurchasesTableColumnId.Seating,
      sectionType
    );

  const { data, hasNoDataLoad } = usePurchaseTableData({
    entities,
    entityCount,
    viewMode,
    isItemsLoading,
    ignoreMaxCount,
  });

  const [pagination, setPagination] = useState<PaginationState | undefined>(
    disablePagination
      ? undefined
      : {
          pageIndex:
            serverSidePageIndex ?? (initState?.pagination?.pageIndex || 0),
          pageSize: pageSize ?? PAGE_SIZE,
        }
  );

  useEffect(() => {
    onMount?.();
  });

  useEffect(() => {
    /**
     * Intent for `onMount` is use with 'windowing' in order to maintain user state
     * when the component is scrolled back into view.
     * Anything that needs to be persisted in `react-table` state should be added here.
     * Only update on unmount to ensure we aren't doing too many re-renders.
     */
    return () => onUnmount?.({ pagination, sorting });
  }, [pagination, sorting, onUnmount]);

  const [visibleRowIds, setVisibleRowIds] = useState<string[]>();

  const { rowSelection, onRowSelectionChange, getRowId, onFilteredRowsChange } =
    usePurchaseTableSelection({
      data,
      groupId,
      viewMode,
      disableSelection,
      onItemSelectionsChanged,
      setVisibleRowIds,
    });

  // Calculate showCheckbox for this specific group (not global)
  const showCheckbox = useMemo(
    () =>
      alwaysShowCheckbox ||
      selectionMode?.mode === MultiSelectScope.AllGroups ||
      purchaseSelection.isSingleGroupMultiSelect,
    [
      alwaysShowCheckbox,
      purchaseSelection.isSingleGroupMultiSelect,
      selectionMode?.mode,
    ]
  );

  const columns = useGetPurchasesTableColumns({
    groupId,
    viewMode,
    columnVisibilityOverride,
    isSideTable,
    showCheckbox: showCheckbox || alwaysShowCheckbox,
    hideActionOnCheckbox: !alwaysShowCheckbox,
    useV2: true,
  });

  const {
    lastSelectedRowRefIndex,
    allowShiftClickSelection,
    isShiftKeyPressed,
    isCtrlOrCmdKeyPressed,
    updateSelectionForRowAndSubRows,
  } = useSingleTableShortcutSelection<PurchaseOrderTicketGroup>({
    isSideTable,
  });

  const onRowShiftSelect = useCallback(
    (
      tableRows: Row<PurchaseOrderTicketGroup | null>[] | undefined,
      row: Row<PurchaseOrderTicketGroup | null>,
      index: number
    ) => {
      if (
        !tableRows ||
        !allowShiftClickSelection ||
        showCheckbox ||
        alwaysShowCheckbox
      ) {
        return;
      }
      const rowId = row.id.toString();
      const newIsSelected = !rowSelection[rowId];

      if (isShiftKeyPressed && lastSelectedRowRefIndex.current !== null) {
        const start = Math.min(lastSelectedRowRefIndex.current, index);
        const end = Math.max(lastSelectedRowRefIndex.current, index);

        const newRowSelection = { ...rowSelection };
        for (let i = start; i <= end; i++) {
          const row = tableRows[i];
          const rowId = getRowId(row.original, i);

          if (row) {
            updateSelectionForRowAndSubRows({
              row,
              rowId,
              newIsSelected,
              newRowSelection,
            });
          }
        }

        lastSelectedRowRefIndex.current = index;
        onRowSelectionChange(newRowSelection);
      } else if (isCtrlOrCmdKeyPressed) {
        const newRowSelection = { ...rowSelection };
        newRowSelection[rowId] = newIsSelected;
        updateSelectionForRowAndSubRows({
          row,
          rowId,
          newIsSelected, // Select the clicked row
          newRowSelection,
        });
        lastSelectedRowRefIndex.current = index;
        onRowSelectionChange(newRowSelection);
      } else {
        // No modifier key pressed, reset selection
        const newRowSelection = {};

        updateSelectionForRowAndSubRows({
          row,
          rowId,
          newIsSelected,
          newRowSelection,
        });

        lastSelectedRowRefIndex.current = index;
        onRowSelectionChange(newRowSelection);
      }
    },
    [
      allowShiftClickSelection,
      alwaysShowCheckbox,
      getRowId,
      isCtrlOrCmdKeyPressed,
      isShiftKeyPressed,
      lastSelectedRowRefIndex,
      onRowSelectionChange,
      rowSelection,
      showCheckbox,
      updateSelectionForRowAndSubRows,
    ]
  );

  const { mainRoute } = useAppContext();
  const inFullPageEventView = isInFullPageEventView(mainRoute);
  const entitiesSummaryFiltered = useMemo(() => {
    if (!entitiesSummary || !hasTableTotalsFeature) {
      return null;
    }

    if (visibleRowIds) {
      // If there's client-side filtering, the best we can do is aggregate the sales and skip those with different currency
      return createAggregatedPurchaseOrderTicketGroup(
        data
          ?.filter((d, i) => d && visibleRowIds.includes(getRowId(d, i)))
          .map((d) => d!)
      );
    }
    return entitiesSummary;
  }, [entitiesSummary, visibleRowIds, data, getRowId, hasTableTotalsFeature]);

  const onPaginationChange = useCallback(
    (updater: Updater<PaginationState>) => {
      if (!pagination) return;

      const newPagination = functionalUpdate(updater, pagination);
      setPagination(newPagination);
      onPaginationChangeCallback?.(newPagination);
    },
    [pagination, onPaginationChangeCallback]
  );

  const onSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      const newSorting = functionalUpdate(updater, sorting);
      setSorting(newSorting);
      onSortingChangeCallback?.(newSorting);
    },
    [setSorting, sorting, onSortingChangeCallback]
  );

  // Memoize state object to prevent VirtuosoTable rerenders
  const tableState = useMemo(
    () => ({
      pagination,
      sorting,
      rowSelection,
      expanded: expandedState,
      columnVisibility: {
        checkbox: Boolean(showCheckbox || alwaysShowCheckbox),
        qualityControlState: isAutoPoQualityControlled ?? false,
      },
    }),
    [
      pagination,
      sorting,
      rowSelection,
      expandedState,
      showCheckbox,
      alwaysShowCheckbox,
      isAutoPoQualityControlled,
    ]
  );

  const showCheckboxState = useMemo(
    () => showCheckbox || alwaysShowCheckbox,
    [showCheckbox, alwaysShowCheckbox]
  );

  // Memoize getSubRows to prevent infinite rerenders
  const getSubRows = useCallback(
    (originalRow: PurchaseOrderTicketGroup | null) => {
      if (
        !originalRow ||
        !originalRow.ticketGroupItems?.length ||
        originalRow.ticketGroupItems?.length < 2
      ) {
        return [];
      }

      return originalRow.ticketGroupItems.map(
        (tgItem) => tgItem as PurchaseOrderTicketGroup
      );
    },
    []
  );

  const getRowCanExpand = useCallback(
    (row: { original: PurchaseOrderTicketGroup | null }) =>
      viewMode === PurchaseViewMode.TileView &&
      !!(
        row.original?.ticketGroupItems?.length &&
        row.original?.ticketGroupItems?.length > 1
      ),
    [viewMode]
  );

  const tableMeta = useMemo(
    () => ({
      sectionType,
      formatOptionOverrides,
      showSelectAllCheckbox:
        selectionMode?.mode === MultiSelectScope.SingleGroup &&
        purchaseSelection.isSingleGroupMultiSelect,
      summaryData: entitiesSummaryFiltered,
      rowSupportsAccordion,
    }),
    [
      sectionType,
      formatOptionOverrides,
      selectionMode?.mode,
      purchaseSelection.isSingleGroupMultiSelect,
      entitiesSummaryFiltered,
      rowSupportsAccordion,
    ]
  );

  if (getDataFail) {
    return (
      <SomethingWentWrong
        message={<Content id={ContentId.FailToLoadListContent} />}
      />
    );
  }

  if (hasNoDataLoad) {
    return (
      <NoData>
        <WarningMessage
          message={
            <FormatContent
              id={FormatContentId.ApplyFilterToReduceCountsToSeeData}
              params={[MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS.toString()]}
            />
          }
        />
      </NoData>
    );
  }

  return data?.length > 0 ? (
    <ColumnResizingContextProvider
      userSettingId={
        viewMode === PurchaseViewMode.EventTileView
          ? UserSetting.PurchaseEventColumnWidths
          : UserSetting.PurchaseColumnWidths
      }
    >
      <Table<PurchaseOrderTicketGroup>
        useVirtuoso={useVirtuoso}
        actionColId={PurchasesTableColumnId.Actions}
        data={data as PurchaseOrderTicketGroup[]}
        columns={columns as ColumnDef<PurchaseOrderTicketGroup>[]}
        pagination={
          disablePagination
            ? undefined
            : { pageSize: pagination?.pageSize ?? PAGE_SIZE, variant: 'input' }
        }
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        sortingFns={{ ...seatingColumnSorting }}
        onRowSelectionChange={onRowSelectionChange}
        alwaysShowCheckbox={alwaysShowCheckbox}
        manualPagination={manualPagination}
        rowCount={rowCount}
        getGroupId={() => groupId}
        onRowShiftSelect={onRowShiftSelect}
        enableRowSelection={
          showCheckbox || alwaysShowCheckbox || inFullPageEventView
        }
        enableMultiRowSelection={showCheckbox || alwaysShowCheckbox}
        enableSubRowSelection={
          alwaysShowCheckbox ||
          (showCheckbox && viewMode !== PurchaseViewMode.TileView)
        }
        getRowId={getRowId}
        getItemId={(data: PurchaseOrderTicketGroup) => getRowId(data, 0)}
        state={tableState}
        meta={tableMeta}
        className={clsx({
          [shiftSelect]: isShiftKeyPressed,
        })}
        getSubRows={getSubRows}
        getRowCanExpand={getRowCanExpand}
        onExpandedChange={setExpandedState}
      />
    </ColumnResizingContextProvider>
  ) : // We should never see this - because the events are filtered to only those that has purchaseOrders
  // So this is to prevent a crash - but this should be looked at
  viewMode === PurchaseViewMode.FlattenedView ? null : (
    <NoData>
      <FormatContent
        id={FormatContentId.NoDataAvailable}
        params={purchasesText}
      />
    </NoData>
  );
}
